/**
 * Newsletter opt-in prompt — Substack-style modal shown to logged-in
 * community members the first time they land on the forum, unless
 * they've already subscribed or dismissed it within the last 7 days.
 *
 * Subscribing calls the existing /api/brevo/add-contact endpoint
 * and flips `protectedData.newsletterSubscribed = true` on the user.
 * Dismissing writes `protectedData.newsletterPromptDismissedAt = now`
 * so we don't pester for 7 days. Both go through the SDK's
 * sdk.currentUser.updateProfile so the change rides on the same
 * write the rest of the profile updates use.
 *
 * Why protectedData (and not privateData): sdk.currentUser.create()
 * silently drops privateData but accepts protectedData on the body,
 * so users who tick the opt-in checkbox at signup get the flag set
 * the moment the account exists — no follow-up updateProfile call,
 * no race with the post-signup community redirect.
 */

import { brevoAPI } from '../util/api';
import { storableError } from '../util/errors';
import { denormalisedResponseEntities } from '../util/data';
import { currentLocale } from '../util/locale';
import { currentUserShowSuccess } from './user.duck';

// ─── Action types ───────────────────────────────────────────────────
export const OPEN = 'app/newsletterPrompt/OPEN';
export const CLOSE = 'app/newsletterPrompt/CLOSE';
export const SUBSCRIBE_REQUEST = 'app/newsletterPrompt/SUBSCRIBE_REQUEST';
export const SUBSCRIBE_SUCCESS = 'app/newsletterPrompt/SUBSCRIBE_SUCCESS';
export const SUBSCRIBE_ERROR = 'app/newsletterPrompt/SUBSCRIBE_ERROR';
export const DISMISS_REQUEST = 'app/newsletterPrompt/DISMISS_REQUEST';
export const DISMISS_SUCCESS = 'app/newsletterPrompt/DISMISS_SUCCESS';

// ─── Reducer ────────────────────────────────────────────────────────
const initialState = {
  isOpen: false,
  subscribeInProgress: false,
  subscribeError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case OPEN:
      return { ...state, isOpen: true };
    case CLOSE:
      return { ...state, isOpen: false };
    case SUBSCRIBE_REQUEST:
      return { ...state, subscribeInProgress: true, subscribeError: null };
    case SUBSCRIBE_SUCCESS:
      return { ...state, subscribeInProgress: false, isOpen: false };
    case SUBSCRIBE_ERROR:
      return { ...state, subscribeInProgress: false, subscribeError: payload };
    case DISMISS_REQUEST:
      return state;
    case DISMISS_SUCCESS:
      return { ...state, isOpen: false };
    default:
      return state;
  }
}

// ─── Action creators ────────────────────────────────────────────────
export const openNewsletterPrompt = () => ({ type: OPEN });
export const closeNewsletterPrompt = () => ({ type: CLOSE });
const subscribeRequest = () => ({ type: SUBSCRIBE_REQUEST });
const subscribeSuccess = () => ({ type: SUBSCRIBE_SUCCESS });
const subscribeError = e => ({ type: SUBSCRIBE_ERROR, payload: e, error: true });
const dismissSuccess = () => ({ type: DISMISS_SUCCESS });

// ─── Helpers ────────────────────────────────────────────────────────
// SessionStorage suppression so navigating Community ↔ Post in one
// tab doesn't keep re-opening the modal if the user just ignored it.
// Persisted dismiss / subscription still flow through privateData
// and survive the tab close.
const SESSION_KEY = 'attractr.newsletter.shownThisSession';
export const markShownThisSession = () => {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      window.sessionStorage.setItem(SESSION_KEY, '1');
    }
  } catch {}
};
export const wasShownThisSession = () => {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return false;
    return window.sessionStorage.getItem(SESSION_KEY) === '1';
  } catch {
    return false;
  }
};

const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * The single decision function for "should we show the modal right
 * now?". Used by Topbar so the rule lives in one place.
 */
export const shouldShowNewsletterPrompt = currentUser => {
  if (!currentUser?.id) return false;
  const profile = currentUser.attributes?.profile || {};
  // Read protectedData primarily (the canonical home for the flag
  // since signup-time consent lands there), but fall back to
  // privateData for users who subscribed before the protectedData
  // switch so they don't get re-prompted.
  const protectedData = profile.protectedData || {};
  const privateData = profile.privateData || {};
  if (protectedData.newsletterSubscribed === true) return false;
  if (privateData.newsletterSubscribed === true) return false;
  const dismissedAt =
    protectedData.newsletterPromptDismissedAt || privateData.newsletterPromptDismissedAt;
  if (dismissedAt) {
    const ts = Date.parse(dismissedAt);
    if (!Number.isNaN(ts) && Date.now() - ts < DISMISS_TTL_MS) return false;
  }
  if (wasShownThisSession()) return false;
  return true;
};

// ─── Thunks ─────────────────────────────────────────────────────────

/**
 * Page-duck-callable hook — checks the conditions and dispatches
 * OPEN if the modal should show. Use from CommunityPage and
 * PostPage loadData so the prompt only triggers on the forum
 * surface, not Account / Profile / etc. Server-safe: skips when
 * window/sessionStorage are unavailable.
 */
export const maybeOpenNewsletterPrompt = () => (dispatch, getState) => {
  if (typeof window === 'undefined') return;
  const { currentUser } = getState().user;
  if (shouldShowNewsletterPrompt(currentUser)) {
    dispatch(openNewsletterPrompt());
  }
};

// Newsletter consent + dismiss timestamp live on protectedData so
// the signup-time write in sdk.currentUser.create() and these
// post-signup updates target the same field. Naming kept for clarity.
const writeProtectedData = (sdk, protectedDataPatch) => {
  const queryParams = { expand: true };
  return sdk.currentUser
    .updateProfile({ protectedData: protectedDataPatch }, queryParams)
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in updateProfile response');
      }
      return entities[0];
    });
};

/**
 * Adds the user to Brevo via the existing endpoint AND flips
 * privateData.newsletterSubscribed = true. The Brevo server route
 * always responds 200 (even on misconfiguration / Brevo errors) but
 * carries a `success: false` flag in the body — we treat that as a
 * failure here so we don't mark the user "subscribed" when Brevo
 * never actually got the contact.
 *
 * Returns { ok: boolean } so the caller can branch (e.g. fire a
 * success toast only when ok === true).
 */
export const subscribeToNewsletter = () => async (dispatch, getState, sdk) => {
  dispatch(subscribeRequest());
  const { currentUser } = getState().user;
  const profile = currentUser?.attributes?.profile || {};
  const email = currentUser?.attributes?.email;
  if (!email) {
    dispatch(subscribeError(storableError(new Error('No email on current user'))));
    return { ok: false };
  }
  try {
    const brevoResponse = await brevoAPI.addContact({
      firstName: profile.firstName || '',
      lastName: profile.lastName || '',
      email,
      lang: currentLocale(),
      createdAt: new Date().toISOString(),
      list: 'newsletter',
    });
    if (brevoResponse && brevoResponse.success === false) {
      throw new Error(brevoResponse.message || 'Failed to subscribe');
    }
    const updatedUser = await writeProtectedData(sdk, { newsletterSubscribed: true });
    dispatch(currentUserShowSuccess(updatedUser));
    dispatch(subscribeSuccess());
    return { ok: true };
  } catch (e) {
    dispatch(subscribeError(storableError(e)));
    return { ok: false };
  }
};

/**
 * Records dismissal in privateData so the 7-day TTL kicks in on
 * subsequent visits. Also sets the session-storage flag so the
 * current tab doesn't re-pop on navigation even before the SDK
 * write lands.
 */
export const dismissNewsletterPrompt = () => async (dispatch, getState, sdk) => {
  markShownThisSession();
  const { currentUser } = getState().user;
  if (!currentUser?.id) {
    dispatch(dismissSuccess());
    return;
  }
  try {
    const updatedUser = await writeProtectedData(sdk, {
      newsletterPromptDismissedAt: new Date().toISOString(),
    });
    dispatch(currentUserShowSuccess(updatedUser));
  } catch (e) {
    // Even if the persisted write fails the session-storage flag keeps
    // the modal quiet for the rest of the tab. We'll retry next visit.
    console.warn('newsletterPrompt: dismiss persistence failed', e?.message);
  }
  dispatch(dismissSuccess());
};
